import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FileText, Plus } from "lucide-react";
import { slugifyTitle } from "@gaole/shared";
import { api, ApiError } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { toast } from "../../stores/toast";
import { useAuth } from "../../stores/auth";
import { useBottomBarActions } from "../../hooks/useBottomBarActions";
import { Badge, Input, PageLoader, Textarea, Toggle, statusColor, useConfirm } from "../../components/ui";
import { DataTable, type Column } from "../../components/DataTable";
import { ListPageShell } from "../../components/shell/ListPageShell";
import { RightSidebarPanel } from "../../components/RightSidebarPanel";

interface CmsPageRow {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  status: "DRAFT" | "PUBLISHED";
  showInFooter: boolean;
  updatedAt: string;
}

export function CmsPages() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();
  const canWrite = can("cms.write");

  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [showInFooter, setShowInFooter] = useState(false);

  const openCreate = () => setCreateOpen(true);
  const closeCreate = () => setCreateOpen(false);

  const listQuery = useQuery({
    queryKey: ["cms-pages"],
    queryFn: () => api<{ items: CmsPageRow[]; total: number }>("/cms/pages?pageSize=100").then((r) => r.items),
  });

  const createMut = useMutation({
    mutationFn: () =>
      api<{ page: CmsPageRow }>("/cms/pages", {
        method: "POST",
        body: { title, slug: slug || slugifyTitle(title), excerpt, showInFooter },
      }),
    onSuccess: (res) => {
      toast("success", "Page created");
      closeCreate();
      setTitle("");
      setSlug("");
      setExcerpt("");
      setShowInFooter(false);
      void queryClient.invalidateQueries({ queryKey: ["cms-pages"] });
      navigate(`/content/pages/${res.page.id}`);
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/cms/pages/${id}`, { method: "DELETE" }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["cms-pages"] }),
    onError: (e: Error) => toast("error", e instanceof ApiError ? e.message : "Delete failed"),
  });

  useBottomBarActions(() => {
    if (!canWrite) return [];
    if (createOpen) {
      return [
        { id: "cancel", label: "Cancel", onClick: closeCreate },
        {
          id: "create",
          label: "Create",
          variant: "primary" as const,
          disabled: !title.trim() || createMut.isPending,
          onClick: () => createMut.mutate(),
        },
      ];
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  const columns = useMemo<Column<CmsPageRow>[]>(
    () => [
      {
        key: "title",
        header: "Title",
        render: (row) => (
          <div>
            <p className="font-medium">{row.title}</p>
            <p className="text-[10px] text-white/40">/pages/{row.slug}</p>
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (row) => <Badge color={statusColor(row.status)}>{row.status}</Badge>,
      },
      {
        key: "footer",
        header: "Footer",
        render: (row) => (row.showInFooter ? "Yes" : "—"),
      },
      {
        key: "updated",
        header: "Updated",
        render: (row) => fmtDate(row.updatedAt),
      },
    ],
    [],
  );

  if (listQuery.isLoading) return <PageLoader />;

  return (
    <>
      {dialog}
      <ListPageShell>
        <DataTable
        columns={columns}
        rows={listQuery.data ?? []}
        rowKey={(r) => r.id}
        exportFilename="cms-pages"
        entityLabel="Page"
        onRowClick={(row) => navigate(`/content/pages/${row.id}`)}
        rowActions={
          canWrite
            ? {
                onEdit: (row) => navigate(`/content/pages/${row.id}`),
                onDelete: (row) =>
                  confirm({
                    title: "Delete page?",
                    message: `Delete "${row.title}" — this cannot be undone.`,
                    confirmLabel: "Delete",
                    danger: true,
                    action: () => deleteMut.mutate(row.id),
                  }),
              }
            : undefined
        }
        toolbar={{
          search: {
            value: "",
            onChange: () => {},
            placeholder: "Search pages…",
            onSubmit: () => {},
          },
        }}
        empty={{
          icon: FileText,
          title: "No pages yet",
          hint: canWrite ? "Create your first storefront article using Create in the bottom bar." : undefined,
        }}
      />
      </ListPageShell>

      <RightSidebarPanel
        open={createOpen}
        title="Create page"
        onClose={closeCreate}
      >
        <div className="sidebar-form">
          <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
          <Input
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            hint={slug ? `/pages/${slug}` : `Auto: /pages/${slugifyTitle(title) || "…"}`}
          />
          <Textarea
            label="Excerpt"
            rows={3}
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            hint="Optional summary for listings and SEO-style previews."
          />
          <Toggle
            label="Show in storefront footer"
            checked={showInFooter}
            onChange={setShowInFooter}
          />
        </div>
      </RightSidebarPanel>
    </>
  );
}
